import { useCallback, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import { FieldContainer } from '../SharedComponents/FieldContainer/FieldContainer'

import { CheckboxField } from './CheckboxField'

export const CheckboxFieldGroup = ({
	name,
	label,
	options,
	value: valueProp,
	defaultValue,
	onChange,
	disabled,
	required,
	fieldMessage,
	feedbackType = 'default',
	containerProps,
	className,
	'data-testid': dataTestId
}) => {
	const groupId = `${name}-checkgroup}`

	const [internalValue, setInternalValue] = useState(() => defaultValue ?? [])
	const isControlled = valueProp !== undefined
	const selected = isControlled ? valueProp : internalValue

	const setSelected = useCallback(
		(next) => {
			if (!isControlled) {
				setInternalValue(next)
			}
			onChange?.(next)
		},
		[isControlled, onChange]
	)

	const handleItemChange = useCallback(
		(optionValue, isChecked) => {
			const next = isChecked
				? [...selected, optionValue]
				: selected.filter((v) => v !== optionValue)
			setSelected(next)
		},
		[selected, setSelected]
	)

	return (
		<FieldContainer
			label={label}
			disabled={disabled}
			fieldMessage={fieldMessage}
			feedbackType={feedbackType}
			containerProps={containerProps}
			required={required}
		>
			<div
				role='group'
				aria-label={label}
				data-testid={dataTestId ?? name}
				className={twMerge('flex flex-col gap-1', className)}
			>
				{options.map((option) => {
					const itemId = `${groupId}-${option.value}`
					const isChecked = selected.includes(option.value)
					const itemDisabled = disabled || option.disabled

					return (
						<CheckboxField
							key={option.value}
							id={itemId}
							name={name}
							value={option.value}
							label={option.label}
							variant='ghost'
							checked={isChecked}
							onCheckedChange={(checked) =>
								handleItemChange(option.value, checked === true)
							}
							disabled={itemDisabled}
							feedbackType={feedbackType}
							data-testid={
								dataTestId
									? `${dataTestId}-${option.value}`
									: `${name}-${option.value}`
							}
						/>
					)
				})}
			</div>
		</FieldContainer>
	)
}

CheckboxFieldGroup.displayName = 'CheckboxFieldGroup'
